/* components/patterns/PaymentMethodModal.jsx — выбор способа оплаты подписки.
   Показывается ПОСЛЕ выбора тарифа/периода, когда пространству доступен хотя бы
   один способ кроме обычной карты. Опции с иконкой и кратким пояснением:
   российской картой (Робокасса, мгновенно), по счёту для юрлиц (Точка,
   банковский перевод) и иностранной картой (из-за рубежа, нерекуррентно —
   без автопродления).
   Видимость опций управляется пропсами showInvoice / showForeign (фиче-флаги).
   onPickCard → карточный checkout; onPickInvoice → форма реквизитов счёта;
   onPickForeign → карточный checkout с method='foreign_card'. */

import React from 'react';
import { Button } from '../ui/Button.jsx';
import { Icon } from '../ui/Icon.jsx';
import { Modal } from '../ui/Modal.jsx';
import { fmt } from '../../lib/format.js';

  function Method({ icon, title, desc, onClick, disabled }) {
    return (
      <button type="button" className="pay-method" onClick={onClick} disabled={disabled}>
        <span className="pay-method__icon"><Icon name={icon} size={20} /></span>
        <span className="pay-method__body">
          <span className="pay-method__title t-label">{title}</span>
          <span className="pay-method__desc t-caption text-mute">{desc}</span>
        </span>
        <Icon name="chevron-right" size={18} className="pay-method__chevron" />
      </button>
    );
  }

  /** Пояснения к карте и счёту — что оплата сделает с тарифом. Чистая функция (vitest).
   *
   * startsAt — акция встаёт в очередь за текущим тарифом (PlanCard.queuedStartsAt,
   * `period_effect.queued` на странице ссылки): «активируется сразу» было бы неправдой —
   * начнётся она в эту дату.
   * extendsTo — ссылка на тот же идущий тариф (`period_effect.extends`): оплата продлевает
   * срок от конца до этой даты, а не включает тариф с сегодня.
   * Ни того ни другого (покупка без идущего срока, гость-плательщик — дат сервер ему не
   * шлёт) — прежний текст «активируется сразу». */
  function methodNotes({ startsAt = null, extendsTo = null } = {}) {
    const card = 'Visa, Mastercard, МИР российского банка, СБП. ';
    const invoice = 'Счёт на организацию, оплата банковским переводом. ';
    if (startsAt) {
      const queued = 'тариф начнёт действовать ' + fmt.date(startsAt) + ', когда закончится текущий.';
      return { card: card + 'Оплата онлайн — ' + queued, invoice: invoice + 'После оплаты ' + queued };
    }
    if (extendsTo) {
      const until = fmt.date(extendsTo) + '.';
      return {
        card: card + 'Оплата онлайн продлит текущий срок до ' + until,
        invoice: invoice + 'После поступления оплаты срок продлится до ' + until,
      };
    }
    return {
      card: card + 'Оплата онлайн — подписка активируется сразу.',
      invoice: invoice + 'Тариф активируется после поступления оплаты.',
    };
  }

  function PaymentMethodModal({
    open, plan, busy = false, showInvoice = true, showForeign = false, startsAt = null,
    extendsTo = null, onPickCard, onPickInvoice, onPickForeign, onClose,
  }) {
    const notes = methodNotes({ startsAt, extendsTo });
    return (
      <Modal open={!!open && !!plan} onClose={busy ? undefined : onClose} size="sm"
        title="Способ оплаты" labelledBy="pay-method-title"
        footerLeft={plan
          ? <span className="t-body-sm text-dim">Тариф «{plan.name}» · <span className="num">{fmt.money(plan.price_rub)}</span></span>
          : null}
        footer={<Button variant="ghost" disabled={busy} onClick={onClose}>Отмена</Button>}>
        <div className="stack stack-12">
          <p className="t-body-sm text-mute">Выберите, как оплатить подписку.</p>
          <div className="pay-methods">
            <Method icon="card" title="Российской картой" desc={notes.card}
              onClick={onPickCard} disabled={busy} />
            {showInvoice && (
              <Method icon="file-text" title="По счёту — для юрлиц (ИП / ООО)" desc={notes.invoice}
                onClick={onPickInvoice} disabled={busy} />
            )}
            {showForeign && (
              <Method icon="globe" title="Иностранной картой"
                desc="Оплата из-за рубежа: Visa или Mastercard иностранного банка. Списание в валюте карты по курсу банка. Без автопродления — тариф продлевается вручную."
                onClick={onPickForeign} disabled={busy} />
            )}
          </div>
        </div>
      </Modal>
    );
  }

export { PaymentMethodModal, methodNotes };
